import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import Button from 'react-bootstrap/lib/Button';
import GeoSuggest from 'react-geosuggest';
import classnames from 'classnames';
import _ from 'lodash';

import StationFinderActions from '../../actions/StationFinderActions';
import t from '../../lib/I18n';
import GoogleMapsHelper from '../../helpers/GoogleMaps';
import { StationFinderSearchInteractions } from '../../constants/AppConstants';

import Icon from '../general/Icon';
import SearchExpander from './SearchExpander';

export default class StationSearchOverlay extends React.PureComponent {
    static propTypes = {
        mapsHelper: PropTypes.instanceOf(GoogleMapsHelper),
        onClear: PropTypes.func.isRequired,
        onSelectPosition: PropTypes.func,
        onSearch: PropTypes.func,
        isMobile: PropTypes.bool,
        isSearchingStations: PropTypes.bool.isRequired,
        isExpanded: PropTypes.bool.isRequired,
        error: PropTypes.instanceOf(Error),
    }

    static defaultProps = {
        onSelectPosition: _.noop,
        onSearch: _.noop,
        isMobile: false,
        error: null,
    }

    state = {
        label: null,
        location: null,
        error: this.props.error,
        isSearchingLocation: false,
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        if (newProps.isExpanded && this.props.isExpanded !== newProps.isExpanded && !this.props.error) {
            this.focusGeoSuggest();
        }

        if (newProps.error && this.props.error !== newProps.error) {
            this.setState({ error: newProps.error });
        }
    }

    canGetCurrentPosition = !!navigator.geolocation;

    componentDidMount() {
        if (this.props.isMobile) {
            this._openSearchOverlay();
        }
    }

    handleSuggest = ({ location, label } ) => {
        this.setState({
            error: null
        });

        this.clearStationSearchError();

        if (!label) {
            this.blurGeoSuggest();
            this.setState({ error: { message: 'stationFinder.noLabel.error' }});
            return;
        }

        if (!location) {
            this.setState({ label });
            this.focusGeoSuggest();
        } else if (location.getCurrentPosition) {
            this.getCurrentPosition();
        } else {
            this.props.onSelectPosition(location);
            const searchInteraction = StationFinderSearchInteractions.ENTERED_LOCATION;
            this.handleSearch(location, searchInteraction);
        }
    };

    handleSuggestFail = (label) => {
        this.blurGeoSuggest();
        this.setState({ error: { message: 'stationFinder.label.error', label }, label});
    };

    blurGeoSuggest () {
        ReactDOM.findDOMNode(this.refs.overlay).focus();
    }

    focusGeoSuggest () {
        this.blurGeoSuggest();
        _.delay(() => this.refs.geoSuggest.focus(), 40);
    }

    focusButton () {
        ReactDOM.findDOMNode(this.refs.button).focus();
    }

    handleSearch = (location, searchInteraction) => {
        if (location && location.lat && location.lng) {
            this.props.onSearch(location, searchInteraction);
        }

        this._closeSearchOverlay();
    }

    handleSearchButton = () => {
        this.refs.geoSuggest.selectSuggest(this.refs.geoSuggest.state.activeSuggest);
    }

    updateLabel = (label) => {
        this.setState({ error: null, label });
        this.clearStationSearchError();
    }

    clearStationSearchError = () => {
        if (this.props.error) {
            StationFinderActions.clearError();
        }
    }

    getCurrentPosition () {
        if (this.canGetCurrentPosition) {
            this.setState({ isSearchingLocation: true });
            navigator.geolocation.getCurrentPosition(this.setCurrentPosition, this.setPositionError);
        }
    }

    setCurrentPosition = (position) => {
        const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
        };

        this.props.onSelectPosition(location);

        const searchInteraction = StationFinderSearchInteractions.CURRENT_LOCATION;
        this.handleSearch(location, searchInteraction);

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location }, (results, status) => {
            if (status === 'OK' && results.length) {
                const { placeId, formatted_address: label } = results[0];
                if (placeId) {
                    this.refs.geoSuggest.update(label);
                }
            }
        });

        this.setState({ isSearchingLocation: false });
    }

    setPositionError = (error) => {
        this.setState({ label: this.refs.geoSuggest.state.userInput });
        const isPermissionDenied = error.code === error.PERMISSION_DENIED;
        const geolocationError = `stationFinder.geolocation.error${ isPermissionDenied ? '.permissionDenied' : ''}`;
        this.setState({ error: { message: geolocationError }, isSearchingLocation: false });
        this.blurGeoSuggest();
    }

    handleSearchClear = () => {
        this._reset();
        this.props.onClear();

        if (!this.props.isMobile) {
            this.focusGeoSuggest();
        }
    }

    _reset () {
        this.setState({
            label: null,
            location: null,
            error: null
        });

        this.clearStationSearchError();

        this.refs.geoSuggest.state.userInput = '';
    }

    getFixtures () {
        let filters = [];
        if (this.canGetCurrentPosition) {
            filters.push({
                className: 'stationFinder__search-current-position otrl otrl-wifi',
                label: t('stationFinder.search.useCurrentPosition'),
                location: { getCurrentPosition: true }
            });
        }
        return filters;
    }

    _closeSearchOverlay () {
        this._reset();

        StationFinderActions.setExpanded(false);
    }

    _openSearchOverlay () {
        StationFinderActions.setExpanded(true);
    }

    toggleSearchOverlay = () => {
        if (this.props.isExpanded) {
            this._closeSearchOverlay();
        } else {
            this._openSearchOverlay();
        }
    }

    _getSearchButton() {
        const findStation = !this.state.error ? this.handleSearchButton : null;
        if (this.state.isSearchingLocation) {
            return (
                <Button ref="button" bsStyle="primary" tabIndex="-1">
                    <Icon className="search-spin" icon="spinner" />
                </Button>
            );
        } else {
            return (
                <Button ref="button" bsStyle="primary" onClick={ findStation } tabIndex="-1">
                    { t('stationFinder.search.buttonText') }
                </Button>
            );
        }
    }

    getStationSearchGroup () {
        const {isExpanded} = this.props;
        return (
            <div
                className="input-group"
                id="search-overlay"
                role="region"
                aria-label={t('stationFinder.search.placeholder')}
            >
                <SearchExpander
                    isExpanded={isExpanded}
                    isSearching={ this.props.isSearchingStations }
                    onExpand={ this.toggleSearchOverlay }/>
                { isExpanded && (
                    <React.Fragment>
                    <GeoSuggest ref="geoSuggest"
                        id="stationFinderSearchInput"
                        label={ t('stationFinder.search.placeholder') }
                        placeholder={ t('stationFinder.search.placeholder') }
                        inputClassName="input-field stationFinder__search-input"
                        country="gb"
                        ignoreTab
                        autoActivateFirstSuggest
                        queryDelay={ 100 }
                        fixtures={ this.getFixtures() }
                        onSuggestSelect={ this.handleSuggest }
                        onSuggestNoResults={ this.handleSuggestFail }
                        onChange={ this.updateLabel } />
                    <span className="input-group-btn">
                        { this._getSearchButton() }
                    </span>
                </React.Fragment>
                ) }
            </div>
        );
    }

    _getSearchOverlayClassname () {
        return classnames('stationFinder__search-overlay', {
            expanded: this.props.isExpanded,
            mobile: this.props.isMobile
        });
    }

    dismissError = () => {
        this.setState({ error: null });
        this.clearStationSearchError();
    }

    render () {
        const { error } = this.state;
        const shouldShowClear = this.state.label && !this.state.isSearchingLocation;

        return (
            <div
                id="stationFinder-overlay"
                className={ this._getSearchOverlayClassname() }
                ref="overlay"
                tabIndex={ -1 }
            >
                { shouldShowClear && <Button className="stationFinder__search-clear" onClick={ () => this.handleSearchClear() }><Icon icon="clear" /></Button> }
                { this.getStationSearchGroup() }
                { error &&
                    <div onClick={ this.dismissError } className="stationFinder__search-error">
                        <div className="icon-margin">
                            <Icon icon="error" />
                        </div>
                        <div className="alert-warning">
                            { t(error.message) }{ error.label }
                        </div>
                    </div> }
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/stationFinder/SearchOverlay.js